import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  type EngagementOutput, ENGAGEMENT_PIPELINE_LABELS, ENGAGEMENT_PIPELINE_VISIBLE_STAGES, ITEM_STATUS_LABELS, DATA_STATUS_LABELS,
  type ComplianceItemOutput, type UserOutput,
} from '@1financial/shared';
import { useAuth, getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { SortableTh, TablePager, useTableControls } from '../components/TableControls.js';
import { FilterBar, EmptyFilterRow, type ActiveFilter } from '../components/FilterBar.js';
import { TableSkeletonRows } from '../components/Skeleton.js';
import { ViewIcon } from '../components/ActionIcons.js';
import { fmtDate } from '../lib/date.js';
import { useUserPreference } from '../lib/user-preferences.js';

type Tab = 'engagements' | 'compliance';

export function MyWorkPage() {
  const { api } = useAuth();
  const [tab, setTab] = useUserPreference<Tab>('my-work.tab', 'engagements', {
    validate: (value) => value === 'engagements' || value === 'compliance',
  });
  const [engPipeline, setEngPipeline] = useUserPreference('my-work.engagementPipeline', '');
  const [compStatus, setCompStatus] = useUserPreference('my-work.complianceStatus', '');
  const eng = useTableControls('updatedAt', 'desc', [engPipeline], 'my-work.engagements');
  const comp = useTableControls('dueDate', 'asc', [compStatus], 'my-work.compliance');

  const engFilters: ActiveFilter[] = [
    ...(engPipeline ? [{ key: 'pipeline', label: `Pipeline: ${ENGAGEMENT_PIPELINE_LABELS[engPipeline as keyof typeof ENGAGEMENT_PIPELINE_LABELS] ?? engPipeline}` }] : []),
  ];
  const compFilters: ActiveFilter[] = [
    ...(compStatus ? [{ key: 'status', label: `Status: ${ITEM_STATUS_LABELS[compStatus as keyof typeof ITEM_STATUS_LABELS] ?? compStatus}` }] : []),
  ];

  const users = useQuery({
    queryKey: ['users', { limit: 100 }],
    enabled: api !== null,
    queryFn: async () => {
      const res = await fetch('/v1/users?limit=100', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: UserOutput[] } };
      return body.data.items;
    },
  });

  const userMap = new Map((users.data ?? []).map((u) => [u.id, u.displayName]));

  const complianceClients = useQuery({
    queryKey: ['compliance-clients', { limit: 100 }],
    enabled: api !== null && tab === 'compliance',
    queryFn: async () => {
      const res = await fetch('/v1/compliance/clients?limit=100', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: { id: string; name: string }[] } };
      return body.data.items;
    },
  });

  const clientMap = new Map((complianceClients.data ?? []).map((c) => [c.id, c.name]));

  const engagements = useQuery({
    queryKey: ['my-engagements', { pipeline: engPipeline, sortBy: eng.sortBy, sortOrder: eng.sortOrder, cursor: eng.cursor }],
    enabled: api !== null && tab === 'engagements',
    queryFn: async () => {
      const p = new URLSearchParams({ assignedToMe: 'true', limit: '100', sortBy: eng.sortBy, sortOrder: eng.sortOrder });
      if (engPipeline) p.set('pipeline', engPipeline);
      if (eng.cursor) p.set('cursor', eng.cursor);
      const res = await fetch(`/v1/engagements?${p.toString()}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: EngagementOutput[]; total: number; nextCursor: string | null } };
      return body.data;
    },
  });

  const compliance = useQuery({
    queryKey: ['my-compliance-items', { status: compStatus, sortBy: comp.sortBy, sortOrder: comp.sortOrder, cursor: comp.cursor }],
    enabled: api !== null && tab === 'compliance',
    queryFn: async () => {
      const p = new URLSearchParams({ assignedToMe: 'true', limit: '100', sortBy: comp.sortBy, sortOrder: comp.sortOrder });
      if (compStatus) p.set('status', compStatus);
      if (comp.cursor) p.set('cursor', comp.cursor);
      const res = await fetch(`/v1/compliance/items?${p.toString()}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: ComplianceItemOutput[]; total: number; nextCursor: string | null } };
      return body.data;
    },
  });

  const TabButton = ({ id, label, count }: { id: Tab; label: string; count?: number }) => (
    <button
      onClick={() => setTab(id)}
      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        tab === id ? 'bg-bg-hover text-white' : 'text-muted hover:text-white hover:bg-bg-hover'
      }`}
    >
      {label} {count != null ? `(${count})` : ''}
    </button>
  );

  return (
    <div className="space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">My Work</h1>
          <p className="text-sm text-muted">Engagements and compliance items assigned to you.</p>
        </div>
      </header>

      <div className="flex gap-2">
        <TabButton id="engagements" label="Engagements" count={engagements.data?.total} />
        <TabButton id="compliance" label="Compliance" count={compliance.data?.total} />
      </div>

      {tab === 'engagements' && (
        <>
        <FilterBar
          activeFilters={engFilters}
          onRemoveFilter={() => setEngPipeline('')}
          onClearAll={() => setEngPipeline('')}
        >
          <select
            value={engPipeline}
            onChange={(e) => setEngPipeline(e.target.value)}
            className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
          >
            <option value="">All pipelines</option>
            {ENGAGEMENT_PIPELINE_VISIBLE_STAGES.map((s) => (
              <option key={s} value={s}>{ENGAGEMENT_PIPELINE_LABELS[s] ?? s}</option>
            ))}
          </select>
        </FilterBar>
        <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[960px]">
            <thead>
              <tr className="text-left text-xs text-muted bg-bg-card border-b border-border">
                <SortableTh label="Title" sortKey="title" sortBy={eng.sortBy} sortOrder={eng.sortOrder} onSort={eng.toggleSort} />
                <SortableTh label="Client" sortKey="client" sortBy={eng.sortBy} sortOrder={eng.sortOrder} onSort={eng.toggleSort} />
                <SortableTh label="Type" sortKey="type" sortBy={eng.sortBy} sortOrder={eng.sortOrder} onSort={eng.toggleSort} />
                <SortableTh label="Jurisdiction" sortKey="jurisdiction" sortBy={eng.sortBy} sortOrder={eng.sortOrder} onSort={eng.toggleSort} />
                <SortableTh label="Pipeline" sortKey="pipeline" sortBy={eng.sortBy} sortOrder={eng.sortOrder} onSort={eng.toggleSort} />
                <th className="px-4 py-3 font-medium">Manager</th>
                <th className="px-4 py-3 font-medium">Assignee</th>
                <SortableTh label="Start" sortKey="startDate" sortBy={eng.sortBy} sortOrder={eng.sortOrder} onSort={eng.toggleSort} />
                <SortableTh label="End" sortKey="endDate" sortBy={eng.sortBy} sortOrder={eng.sortOrder} onSort={eng.toggleSort} />
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {engagements.isLoading && <TableSkeletonRows colSpan={10} />}
              {engagements.error && (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-rose-400">{(engagements.error as Error).message}</td></tr>
              )}
              {engagements.data?.items.length === 0 && (
                engFilters.length > 0
                  ? <EmptyFilterRow colSpan={10} onClearAll={() => setEngPipeline('')} />
                  : <tr><td colSpan={10} className="px-4 py-8 text-center text-muted">No assigned engagements.</td></tr>
              )}
              {engagements.data?.items.map((e) => (
                <tr key={e.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                  <td className="px-4 py-3 text-white">{e.title ?? 'Untitled'}</td>
                  <td className="px-4 py-3 text-muted">{e.clientCompany ?? e.clientName ?? '—'}</td>
                  <td className="px-4 py-3 text-muted">{e.type ?? '—'}</td>
                  <td className="px-4 py-3 text-muted">{e.jurisdiction ?? '—'}</td>
                  <td className="px-4 py-3 text-muted">{ENGAGEMENT_PIPELINE_LABELS[e.pipeline] ?? e.pipeline}</td>
                  <td className="px-4 py-3 text-muted">{e.managerId ? (userMap.get(e.managerId) ?? '—') : '—'}</td>
                  <td className="px-4 py-3 text-muted">{e.agentId ? (userMap.get(e.agentId) ?? '—') : '—'}</td>
                  <td className="px-4 py-3 text-muted">{fmtDate(e.startDate)}</td>
                  <td className="px-4 py-3 text-muted">{fmtDate(e.endDate)}</td>
                  <td className="px-4 py-3 text-right">
                    <ViewIcon to={`/engagements/${e.id}`} label="View engagement" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePager
            pageIndex={eng.pageIndex}
            count={engagements.data?.items.length ?? 0}
            limit={100}
            total={engagements.data?.total}
            hasNext={Boolean(engagements.data?.nextCursor)}
            onPrev={eng.goPrev}
            onNext={() => eng.goNext(engagements.data?.nextCursor)}
          />
        </div>
        </>
      )}

      {tab === 'compliance' && (
        <>
        <FilterBar
          activeFilters={compFilters}
          onRemoveFilter={() => setCompStatus('')}
          onClearAll={() => setCompStatus('')}
        >
          <select
            value={compStatus}
            onChange={(e) => setCompStatus(e.target.value)}
            className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
          >
            <option value="">All statuses</option>
            {Object.keys(ITEM_STATUS_LABELS).map((s) => (
              <option key={s} value={s}>{ITEM_STATUS_LABELS[s as keyof typeof ITEM_STATUS_LABELS] ?? s}</option>
            ))}
          </select>
        </FilterBar>
        <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead>
              <tr className="text-left text-xs text-muted bg-bg-card border-b border-border">
                <SortableTh label="Service" sortKey="serviceType" sortBy={comp.sortBy} sortOrder={comp.sortOrder} onSort={comp.toggleSort} />
                <SortableTh label="Client" sortKey="client" sortBy={comp.sortBy} sortOrder={comp.sortOrder} onSort={comp.toggleSort} />
                <SortableTh label="Jurisdiction" sortKey="jurisdiction" sortBy={comp.sortBy} sortOrder={comp.sortOrder} onSort={comp.toggleSort} />
                <SortableTh label="Status" sortKey="status" sortBy={comp.sortBy} sortOrder={comp.sortOrder} onSort={comp.toggleSort} />
                <SortableTh label="Data" sortKey="dataStatus" sortBy={comp.sortBy} sortOrder={comp.sortOrder} onSort={comp.toggleSort} />
                <SortableTh label="End date" sortKey="deadline" sortBy={comp.sortBy} sortOrder={comp.sortOrder} onSort={comp.toggleSort} />
                <SortableTh label="Due date" sortKey="dueDate" sortBy={comp.sortBy} sortOrder={comp.sortOrder} onSort={comp.toggleSort} />
                <th className="px-4 py-3 font-medium">Manager</th>
                <th className="px-4 py-3 font-medium">Assignee 1</th>
                <th className="px-4 py-3 font-medium">Assignee 2</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {compliance.isLoading && <TableSkeletonRows colSpan={11} />}
              {compliance.error && (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-rose-400">{(compliance.error as Error).message}</td></tr>
              )}
              {compliance.data?.items.length === 0 && (
                compFilters.length > 0
                  ? <EmptyFilterRow colSpan={11} onClearAll={() => setCompStatus('')} />
                  : <tr><td colSpan={11} className="px-4 py-8 text-center text-muted">No assigned compliance items.</td></tr>
              )}
              {compliance.data?.items.map((item) => (
                <tr key={item.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                  <td className="px-4 py-3 text-white">{item.serviceType}</td>
                  <td className="px-4 py-3 text-muted">{item.client?.name ?? clientMap.get(item.clientId) ?? '—'}</td>
                  <td className="px-4 py-3 text-muted">{item.jurisdiction ?? '—'}</td>
                  <td className="px-4 py-3 text-muted">{ITEM_STATUS_LABELS[item.status] ?? item.status}</td>
                  <td className="px-4 py-3 text-muted">{item.dataStatus ? (DATA_STATUS_LABELS[item.dataStatus] ?? item.dataStatus) : '—'}</td>
                  <td className="px-4 py-3 text-muted">{fmtDate(item.deadline)}</td>
                  <td className="px-4 py-3 text-muted">{fmtDate(item.dueDate)}</td>
                  <td className="px-4 py-3 text-muted">{item.managerId ? (userMap.get(item.managerId) ?? '—') : '—'}</td>
                  <td className="px-4 py-3 text-muted">{item.assignee1Id ? (userMap.get(item.assignee1Id) ?? '—') : '—'}</td>
                  <td className="px-4 py-3 text-muted">{item.assignee2Id ? (userMap.get(item.assignee2Id) ?? '—') : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <ViewIcon to={`/compliance/${item.id}`} label="View item" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePager
            pageIndex={comp.pageIndex}
            count={compliance.data?.items.length ?? 0}
            limit={100}
            total={compliance.data?.total}
            hasNext={Boolean(compliance.data?.nextCursor)}
            onPrev={comp.goPrev}
            onNext={() => comp.goNext(compliance.data?.nextCursor)}
          />
        </div>
        </>
      )}
    </div>
  );
}
